import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileOutlined } from '@ant-design/icons';
import { message, Upload, UploadProps } from 'antd';
import { MultipleFileListItem } from './__partials/MultipleFileListItem';
import * as Styled from './styles';
import { convertToBase64 } from 'utils/converToBase64';
import { isImage } from 'utils/isImage';
import { getImagesObject } from 'utils/nft/get-image-object.util';
import { CustomEvents } from 'constants/dom/custom-events';
import type { UploadFile } from 'antd/es/upload/interface';
import { IEditNftValidateImageParams, TButtonSize } from 'interfaces';

type UploadDocumentProps = Omit<
  UploadProps,
  | 'accept'
  | 'showUploadList'
  | 'customRequest'
  | 'openFileDialogOnClick'
  | 'onChange'
  | ''
> & {
  accept: string;
  maxFileSize: number;
  valuePropName?: string;
  title: string;
  description?: string;
  multiSelect?: boolean;
  maxFileUpload?: number;
  handleReturnPreview?: (arg: string) => void;
  externalFileList?: Partial<UploadFile>[];
  uploadButton?: {
    size: TButtonSize;
  };
  handleUploadError?: () => void;
  minWidth?: number;
  minHeigth?: number;
  externalPromise?: (
    params: IEditNftValidateImageParams
  ) => Promise<boolean> | undefined;
  landscapeOnly?: boolean;
  allowedType?: string;
  nftId?: string;
};

export const UploadDocument = ({
  accept,
  maxFileSize,
  title,
  description,
  multiSelect = false,
  maxFileUpload = 1,
  handleReturnPreview,
  externalFileList,
  uploadButton = {
    size: 'normal'
  },
  handleUploadError,
  minWidth,
  minHeigth,
  landscapeOnly,
  allowedType,
  nftId,
  externalPromise,
  ...restProps
}: UploadDocumentProps) => {
  const [canUploadDialogOpen, setCanUploadDialogOpen] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [singleFilePreviewOpen, setSingleFilePreviewOpen] = useState(false);
  const customRequestErroredFileIdReference = useRef('');
  const fileListPresentable = ((fileList.length > 0
    ? fileList
    : externalFileList) || []) as UploadFile[];

  const { t } = useTranslation();

  useEffect(() => {
    if (
      !multiSelect &&
      handleReturnPreview &&
      fileListPresentable[0]?.preview
    ) {
      handleReturnPreview(fileListPresentable[0]?.preview);
    }
  }, [fileListPresentable, multiSelect, handleReturnPreview]);

  useEffect(() => {
    if (!externalFileList) return;
    /*
     * this check ensures that files passed from external source were uploaded
     * previously
     */
    if (
      externalFileList.every(
        file =>
          file?.preview?.startsWith?.('data:image/') ||
          file?.preview?.startsWith?.('http')
      )
    ) {
      setFileList(externalFileList as UploadFile[]);
    }
  }, [externalFileList]);

  useEffect(() => {
    const filesUpdatedEvent = new CustomEvent(CustomEvents.filesUpdated.value, {
      bubbles: true,
      detail: {
        [CustomEvents.filesUpdated.detail]: fileList
      }
    });
    document.dispatchEvent(filesUpdatedEvent);
  }, [fileList]);

  const constructErrorMessage = (
    minWidth: number,
    minHeight: number,
    landscapeOnly?: boolean
  ) => {
    const additionalDimensions =
      !landscapeOnly && minWidth !== minHeight
        ? t('generic.fileDimensionsErrorAlt', {
            height: minHeight,
            width: minWidth
          })
        : '';

    return `${t('generic.fileDimensionsError', {
      width: minWidth,
      height: minHeight
    })}${additionalDimensions}`;
  };

  const validateFileDimensions = async (
    file: File,
    minWidth = 0,
    minHeight = 0
  ) => {
    const IMAGE_TYPE_PREFIX = 'image/';
    const IMAGE_TYPE_GIF = 'image/gif';
    const docTypes = allowedType?.split(',');

    await new Promise<void>((resolve, reject) => {
      if (
        file.type.startsWith(IMAGE_TYPE_PREFIX) &&
        file.type !== IMAGE_TYPE_GIF
      ) {
        const image = new Image();
        image.src = URL.createObjectURL(file);

        const landscapeOrientation = () =>
          image.width >= minWidth && image.height >= minHeight;

        const anyOrientation = () =>
          (image.width >= minWidth && image.height >= minHeight) ||
          (image.width >= minHeight && image.height >= minWidth);

        image.onload = () => {
          if (landscapeOnly ? landscapeOrientation() : anyOrientation()) {
            resolve();
          } else {
            reject(
              new Error(
                constructErrorMessage(minWidth, minHeight, landscapeOnly)
              )
            );
          }
        };
        image.onerror = error => {
          reject(new Error(`${'generic.fileUploadBrowserError'}: ${error}`));
        };
      } else if (docTypes?.includes(file.type)) {
        const reader = new FileReader();
        reader.onload = () => {
          reader.result as ArrayBuffer;
          resolve();
        };
        reader.onerror = error => {
          reject(new Error(`${'generic.fileUploadBrowserError'}: ${error}`));
        };
        reader.readAsArrayBuffer(file);
      } else {
        return;
      }
    });
  };

  const validateFileType = (type: string) => {
    if (!accept.split(',').includes(type)) {
      throw new Error(t('generic.fileTypeError'));
    }
  };

  const validateFileSize = (size: number) => {
    if (size > maxFileSize * 1024 * 1024) {
      throw new Error(`${t('generic.fileSizeError')} ${maxFileSize}Mb`);
    }
  };

  const generateFilePreview = async (file: File & UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = (await convertToBase64(file)) as string;
    }
  };

  const updateFileStates = (file: UploadFile) => {
    setFileList(prevState => (multiSelect ? [...prevState, file] : [file]));
  };

  const handleSuccess = (onSuccess: any) => {
    onSuccess('ok');
  };

  const customRequest = async (data: any) => {
    try {
      validateFileType(data.file.type);
      await validateFileDimensions(data.file, minWidth, minHeigth);
      validateFileSize(data.file.size);
      await generateFilePreview(data.file);
      if (!multiSelect) {
        updateFileStates(data.file);
      }
    } catch (error) {
      customRequestErroredFileIdReference.current = data.file.uid;
      handleUploadError?.();
      if (typeof error === 'string') {
        message.error(error);
      } else if (error instanceof Error) {
        message.error(error.message);
      }
      return;
    }
    handleSuccess(data.onSuccess);
  };
  const handleChange: UploadProps['onChange'] = async ({
    fileList: newFileList,
    file
  }) => {
    let response;
    if (nftId && externalPromise) {
      const params: IEditNftValidateImageParams = {
        id: nftId,
        ...getImagesObject([file.originFileObj] || [], true)
      };
      response = await externalPromise?.(params);
    }

    if ((response === true && externalPromise) || !externalPromise) {
      if (multiSelect) {
        const currentFileList = newFileList
          .map(singleFile => {
            if (singleFile.originFileObj) {
              return singleFile.originFileObj;
            }
            return singleFile;
          })
          .filter(
            file => file.uid !== customRequestErroredFileIdReference.current
          );
        setFileList(currentFileList);
      } else {
        setFileList([file]);
      }
      customRequestErroredFileIdReference.current = '';
    }
  };

  const handleDelete = useCallback(
    (file: UploadFile) => {
      const updatedData = fileList.filter(item => item.uid !== file.uid);
      setFileList(updatedData);
    },
    [fileList]
  );

  const UploadButton = useMemo(() => {
    return multiSelect ? (
      <Styled.UploadMultipleBtn>
        <Styled.ButtonText>{title}</Styled.ButtonText>
        <Styled.UploadIcon />
      </Styled.UploadMultipleBtn>
    ) : (
      <Styled.UploadButtonWrapper $size={uploadButton.size}>
        <Styled.UploadIcon />
        <Styled.UploadTitle>{title}</Styled.UploadTitle>
      </Styled.UploadButtonWrapper>
    );
  }, [multiSelect, uploadButton]);

  const singleItemRender = useMemo(() => {
    return (
      <Styled.ImageContainer $size={uploadButton.size}>
        {isImage(fileListPresentable[0]?.type || '') ? (
          <Styled.FileImageSingle
            src={fileListPresentable[0].preview}
            alt={fileListPresentable[0].name}
            width="100%"
            height="100%"
            preview={{
              visible: singleFilePreviewOpen,
              onVisibleChange: setSingleFilePreviewOpen
            }}
          />
        ) : (
          <>
            <FileOutlined />
            <Styled.FileName>{fileListPresentable[0]?.name}</Styled.FileName>
          </>
        )}
        <Styled.Overlay className="overlay">
          {isImage(fileListPresentable[0]?.type || '') && (
            <Styled.PreviewIcon
              onClick={() => {
                setSingleFilePreviewOpen(true);
              }}
            />
          )}
          <Styled.UploadEditWrapper
            onMouseEnter={() => setCanUploadDialogOpen(true)}
            onMouseLeave={() => setCanUploadDialogOpen(false)}
          >
            <Styled.UploadEditIcon />
          </Styled.UploadEditWrapper>
        </Styled.Overlay>
      </Styled.ImageContainer>
    );
  }, [fileList, singleFilePreviewOpen]);

  return (
    <Styled.Wrapper>
      <Styled.TopWrapper $multiSelect={multiSelect}>
        {description && (
          <Styled.DescriptionWrapper
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
        <Upload
          fileList={fileList}
          accept={accept}
          showUploadList={false}
          beforeUpload={(_file, _fileList) => {
            if (fileList.some(file => file.name === _file.name)) {
              return Upload.LIST_IGNORE;
            }
            return true;
          }}
          customRequest={customRequest}
          openFileDialogOnClick={
            multiSelect || !fileListPresentable.length
              ? true
              : canUploadDialogOpen
          }
          {...restProps}
          {...(multiSelect && {
            onChange: handleChange
          })}
        >
          {fileListPresentable.length >= maxFileUpload
            ? multiSelect
              ? null
              : singleItemRender
            : UploadButton}
        </Upload>
      </Styled.TopWrapper>
      {multiSelect && (
        <Styled.ListWrapper className="multiselect">
          {fileListPresentable.map(singleFile => (
            <MultipleFileListItem
              key={singleFile.uid}
              file={singleFile}
              handleDelete={handleDelete}
            />
          ))}
        </Styled.ListWrapper>
      )}
    </Styled.Wrapper>
  );
};
